<template>
  <div class="flex justify-center">
    <BaseCard class="sm:w-6/12 text-center m-2">
      <div class="mt-2 text-right">
        <router-link :to="entryLinkTo" class="mx-2">
          <BaseButton class="text-xs" buttonClass="outlined">{{
            pointChargeButtonLabel
          }}</BaseButton>
        </router-link>
      </div>
      <div class="w-full flex justify-center">
        <!-- ref: https://medium.com/@heyoka/scratch-made-svg-donut-pie-charts-in-html5-2c587e935d72 -->
        <svg class="w-5/6 h-5/6" :viewBox="`0 0 ${VIEW_LENGTH} ${VIEW_LENGTH}`">
          <circle
            class="text-white fill-current"
            :cx="CX"
            :cy="CY"
            :r="RADIUS"
          />
          <circle
            class="text-gray-400 stroke-current"
            :cx="CX"
            :cy="CY"
            :r="RADIUS"
            fill="transparent"
            :stroke-width="strokeWidth"
          />
          <circle
            class="text-primary-light stroke-current"
            :cx="CX"
            :cy="CY"
            :r="RADIUS"
            fill="transparent"
            :stroke-width="strokeWidth"
            :stroke-dasharray="strokeDasharray"
            :stroke-dashoffset="CIRCUMFERENCE / 4"
          />
          <g>
            <text
              x="50%"
              y="35%"
              text-anchor="middle"
              :font-size="RADIUS / 8"
              class="text-gray-500 fill-current"
            >
              {{ pointLabelTitle }}
            </text>
            <text
              x="50%"
              y="50%"
              font-weight="bold"
              text-anchor="middle"
              class="text-primary-main fill-current"
              dominant-baseline="middle"
            >
              <tspan :font-size="RADIUS / 4">{{ walletPoint }}</tspan>
            </text>
            <text
              x="50%"
              y="65%"
              text-anchor="middle"
              :font-size="RADIUS / 8"
              class="text-gray-500 fill-current"
              v-if="nextExchangePoint"
            >
              <tspan x="50%" y="65%">{{ nextExchangeNeedPointPrefix }}</tspan>
              <tspan x="50%" y="75%">
                {{ nextExchangePoint }}{{ nextExchangeNeedPointSuffix }}
              </tspan>
            </text>
            <text
              x="50%"
              y="65%"
              text-anchor="middle"
              :font-size="RADIUS / 8"
              class="text-gray-500 fill-current"
              v-else
            >
              {{ allExchangeEnalbe }}
            </text>
          </g>
        </svg>
      </div>
      <div class="mb-2">
        <ExchangeCards />
      </div>
    </BaseCard>
  </div>
</template>
<script lang="ts">
import { defineComponent, PropType, ref, computed } from "vue";
import { useRoute } from "vue-router";
import ExchangeCards from "../shared/ExchangeCards.vue";
import { useStore } from "@consumer/store";
import { Wallet } from "@consumer/services/api/internalApi/wallets";
import lotterButton from "@assets/lottery_button.svg";
import { routeNames } from "@consumer/router/routeNames";
import { formatPointNumber } from "@shared/utils/formatPointNumber";

export default defineComponent({
  name: "Status",
  components: {
    ExchangeCards,
  },
  props: {
    wallet: {
      type: Object as PropType<Wallet>,
      required: true,
    },
  },
  setup() {
    const lotteryLogo = ref(lotterButton);
    const route = useRoute();
    const store = useStore();
    const entryLinkTo = computed(() => ({
      name: routeNames.CAMPAING_ENTRY,
      params: { campaign_url_code: route.params.campaign_url_code },
    }));

    const pointChargeButtonLabel = computed(() =>
      store.getters["campaign/getCustomizingWord"](
        "my_page.status.point_charge_button_label",
        "ポイントをためる"
      )
    );
    const pointLabelTitle = computed(() =>
      store.getters["campaign/getCustomizingWord"](
        "my_page.status.point_label_title",
        "あなたのポイントは"
      )
    );
    const nextExchangeNeedPointPrefix = computed(() =>
      store.getters["campaign/getCustomizingWord"](
        "my_page.status.next_exchange_need_point_prefix",
        "次まであと"
      )
    );
    const nextExchangeNeedPointSuffix = computed(() =>
      store.getters["campaign/getCustomizingWord"](
        "my_page.status.next_exchange_need_point_suffix",
        "ポイント"
      )
    );
    const allExchangeEnalbe = computed(() =>
      store.getters["campaign/getCustomizingWord"](
        "my_page.status.all_exchange_enable",
        "全てに応募可能です"
      )
    );

    return {
      lotteryLogo,
      entryLinkTo,
      pointChargeButtonLabel,
      pointLabelTitle,
      nextExchangeNeedPointPrefix,
      nextExchangeNeedPointSuffix,
      allExchangeEnalbe,
      ...useGraph(),
    };
  },
});

const useGraph = () => {
  const store = useStore();
  const wallet = computed(() => store.state.wallet);
  const exchangeConfigs = computed(() => {
    const exchangeConfigs =
      store.getters["campaign/formatted_visible_exchange_configs"];
    return exchangeConfigs.length > 0
      ? exchangeConfigs
      : store.getters[
          "campaign/formatted_visible_exchange_config_groups"
        ].flatMap(
          (exchangeConfigGroup) => exchangeConfigGroup.exchange_configs
        );
  });

  const VIEW_LENGTH = 100;
  const CX = VIEW_LENGTH / 2;
  const CY = VIEW_LENGTH / 2;
  const RADIUS = CX - 10;
  const CIRCUMFERENCE = 2 * RADIUS * Math.PI;
  const strokeWidth = RADIUS / 5;

  const nextExchangeableConfigIndex = computed(() =>
    exchangeConfigs.value.findIndex(
      (exchangeConfig) => wallet.value.point < exchangeConfig.consumption_point
    )
  );

  const nextExchangeableConfig = computed(
    () => exchangeConfigs.value[nextExchangeableConfigIndex.value]
  );

  const exchangeableConfig = computed(
    () => exchangeConfigs.value[nextExchangeableConfigIndex.value - 1]
  );

  const exchangeableConfigConsumptionPoint = computed(() => {
    return exchangeableConfig.value
      ? exchangeableConfig.value.consumption_point
      : 0;
  });

  const goalPoint = computed(() => {
    if (!nextExchangeableConfig.value) {
      // wallet.point が全ての ExchangeConfig.consumption_point より高い場合、グラフを塗りつぶすために 1 にしています
      return 1;
    }
    return (
      nextExchangeableConfig.value.consumption_point -
      exchangeableConfigConsumptionPoint.value
    );
  });

  const reachedPoint = computed(
    () => wallet.value.point - exchangeableConfigConsumptionPoint.value
  );

  const scale = computed(() => CIRCUMFERENCE / goalPoint.value);
  const strokeDasharray = computed(
    () =>
      `${reachedPoint.value * scale.value} ${
        CIRCUMFERENCE - reachedPoint.value * scale.value
      }`
  );

  const walletPoint = computed(() => formatPointNumber(wallet.value.point));
  const nextExchangePoint = computed(
    () =>
      nextExchangeableConfig.value &&
      formatPointNumber(
        nextExchangeableConfig.value.consumption_point - wallet.value.point
      )
  );

  return {
    walletPoint,
    nextExchangePoint,
    VIEW_LENGTH,
    CX,
    CY,
    RADIUS,
    CIRCUMFERENCE,
    strokeDasharray,
    strokeWidth,
  };
};
</script>
